/**
 * What the Health report remembers while its overview is swapped out for an
 * area's tabs (the overview unmounts, so its own state would be lost):
 *
 * - the open state of a disclosure that passes `rememberKey` (hosts key it by
 *   report id, so a new report starts closed again);
 * - the "What we checked" row that opened the area, so the web report can
 *   hand focus back to it on Back;
 * - and how "Review N suggestions" reveals the suggestion list.
 *
 * Module state, shared by native and web; nothing here is persisted.
 */
import { Platform } from 'react-native';
import type { CvHealthArea } from '@tabbio/types';

/** Plenty for one session; the oldest key goes first past this. */
const MAX_REMEMBERED = 40;

const openByKey = new Map<string, boolean>();
let areaReturn: { reportId: string; area: CvHealthArea; back: boolean } | null = null;

export function getRememberedOpen(key: string | undefined, fallback: boolean): boolean {
  if (!key) return fallback;
  return openByKey.get(key) ?? fallback;
}

export function rememberOpen(key: string | undefined, open: boolean): void {
  if (!key) return;
  openByKey.delete(key);
  openByKey.set(key, open);
  if (openByKey.size > MAX_REMEMBERED) {
    const oldest = openByKey.keys().next().value;
    if (oldest !== undefined) openByKey.delete(oldest);
  }
}

/** A "What we checked" row opened its area. */
export function noteAreaOpened(reportId: string, area: CvHealthArea): void {
  areaReturn = { reportId, area, back: false };
}

/** The area's own Back (its button, or Escape) is returning to the overview. */
export function noteAreaBack(): void {
  if (areaReturn) areaReturn.back = true;
}

/**
 * The row to focus when this report's overview comes back, once. Only after
 * the area's Back: an overview that comes back any other way (Continue, then
 * Back into the step) keeps focus where the page puts it.
 */
export function takeAreaReturn(reportId: string): CvHealthArea | null {
  const pending = areaReturn;
  areaReturn = null;
  return pending?.back && pending.reportId === reportId ? pending.area : null;
}

/** The "View individual suggestions" disclosure, for `revealSuggestions`. */
export const SUGGESTIONS_TEST_ID = 'cv-health-suggestions';

/**
 * The card's "Review N suggestions" when no suggestion has a change to
 * review: open the list and, on web, bring it into view, so the tap always
 * shows something (the list can already be open, or below the fold).
 */
export function revealSuggestions(setOpen: (open: boolean) => void): void {
  setOpen(true);
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  requestAnimationFrame(() => {
    document
      .querySelector(`[data-testid="${SUGGESTIONS_TEST_ID}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });
}
